import { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ChannelList } from '@components/channel-list';
import { DEBOUNCE_VARIANT_DARK } from '@components/debounced-input-search/src/debounced-input-search.type';
import { ConfirmModal, SearchItemModal } from '@components/modals';
import { RadioListModal } from '@components/modals/radio-list-modal';
import { SubmenuIcon } from '@components/submenu-with-icon';
import { SwitchToggle } from '@components/switch-toggle';
import { MAX_LENGTH_GROUP_NAME } from '@constants/admission-groups';
import { DATE_FORMAT_LONG_DATE_WITH_TIME } from '@constants/datetime';
import { ADMISSION_STATUS_HIDDEN, ADMISSION_STATUS_IN_REVIEW, ADMISSION_STATUS_ON_SALE } from '@domain/admission';
import { Channel, EventChannel } from '@domain/channel';
import { QUERY_KEYS } from '@hooks/admissions/constants';
import { ADMISSION_CARD_TYPE } from '@hooks/admissions/type';
import { useAdmissionCardActions } from '@hooks/admissions/use-admission-card-actions';
import { useGetChannels } from '@hooks/events/use-get-event-channels';
import { useAddOfferToGroup } from '@hooks/groups/use-add-offer-to-group';
import { useCreateGroup } from '@hooks/groups/use-create-group';
import { useRemoveOfferFromGroup } from '@hooks/groups/use-remove-offer-from-group';
import { useGetActiveChannels } from '@hooks/use-active-channels';
import { useDragHandle } from '@hooks/use-drag-handle';
import { useGetSharePaymentLink } from '@hooks/use-get-share-payment-link/use-get-share-payment-link';
import { useDestinationHeight } from '@stores/drag-height-store';
import { useQueryClient } from '@tanstack/react-query';
import { formatDateTime } from '@utils/date-time-format';
import { getLanguageTranslationKey } from '@utils/get-language-trandlation-key/get-language-translation-key';
import { fromBackend } from '@utils/map-date/from-backend';
import { formatCurrency } from '@utils/price-formatter/price-formatter';
import { getGroupNameErrorForSelection } from '@utils/validate-group-name/validate-group-name';
import classNames from 'classnames';

import { AdmissionCardInfo } from '../components/admission-card-info';
import { AdmissionPlaceholderDragEnd } from '../components/admission-placeholder-drag-end';
import { AdmissionPlaceholderDragStart } from '../components/admission-placeholder-drag-start';

import { Props } from './admission-card.type';

// eslint-disable-next-line complexity
export const AdmissionCard = ({
  id,
  timezone,
  name,
  description,
  quantity,
  soldItems,
  currency,
  isPublished,
  isPrivate,
  needsUserVerified,
  amount,
  onlinePrice,
  ddpPrice,
  salesEnds,
  status,
  tags,
  color,
  defaultChannel,
  showSubmenuIcon = true,
  onEdit,
  onDelete,
  onDeletePass,
  onDuplicate,
  onClick,
  onChange,
  onUnassign,
  guestCapacity,
  isPreset,
  isPastEvent,
  checked,
  isSelectActive,
  isMultiSelectActive,
  deleteIsUnassign,
  eventId,
  accessValidity,
  isDragActive,
  isInDragOverlay = false,
  passHolders,
  isActive,
  onToggleClick,
  onClickViewHolders,
  onClickAddHolder,
  onClickViewEvents,
  isPassCard = false,
  dragHandle,
  sectionGroups,
  sectionId,
  isGroupCard = false,
  groupId,
  admissionTypeLabel,
  isDeleting = false,
  isCreating = false,
  isEditing = false,
  hideStockInfo = false,
  submenuZIndexClass,
}: Props) => {
  // Use custom hook to handle drag and drop with backward compatibility
  const { attributes, listeners, setNodeRef, isDragging, insertPosition, containerRef, getInitialHeight } =
    useDragHandle({
      id,
      dragHandle,
    });

  // Get shared drag height from Zustand store
  const destinationHeight = useDestinationHeight();

  const isDateVisible = !!salesEnds && status === ADMISSION_STATUS_ON_SALE;
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const locale = i18n.language;

  // PassCard states
  const [isToggleActive, setIsToggleActive] = useState(!!isActive);
  const [isConfirmToggleModalOpen, setIsConfirmToggleModalOpen] = useState(false);
  const isSharePaymentLinkActive = isPublished && !isPrivate && status === ADMISSION_STATUS_ON_SALE;
  const formattedPrice = formatCurrency({
    currency,
    number: amount,
  });
  const formattedOnlinePrice = formatCurrency({
    currency,
    number: onlinePrice ?? 0,
  });
  const formatedDdpPrice = formatCurrency({
    currency,
    number: ddpPrice ?? 0,
  });

  const subtitleModalAddToGroup = (
    <Trans
      i18nKey='admissionGroups:addAdmissionToGroupModalSubtitle'
      components={{ i: <i /> }}
      values={{ AdmissionName: name }}
    />
  );

  const salesEndDate = salesEnds ? fromBackend(salesEnds) : null;
  const formattedSalesEndsDate = salesEndDate
    ? formatDateTime({
        date: salesEndDate.toDate(),
        locale,
        format: DATE_FORMAT_LONG_DATE_WITH_TIME,
        timezone,
      })
    : '';
  const isOpenForAll = isPublished && !isPrivate && !needsUserVerified;
  const [isOpenSelectPaymentLinkChannelModal, setIsOpenSelectPaymentLinkChannelModal] = useState(false);
  const [selectedPaymentLinkChannel, setSelectedPaymentLinkChannel] = useState<Channel | undefined>();
  const [searchPaymentLinkChannelsValue, setSearchPaymentLinkChannelsValue] = useState('');
  const [isSharedSubMenuClicked, setIsSharedSubMenuClicked] = useState(false);
  const [paymentLinkChannels, setPaymentLinkChannels] = useState<EventChannel[] | undefined>();
  const [isOpenAddGroupModal, setIsOpenAddGroupModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<{ id?: string; name: string } | undefined>();
  const [errorAddGroup, setErrorAddGroup] = useState('');

  const { isShareLinkclick, setIsShareLinkclick } = useGetSharePaymentLink({
    channel: selectedPaymentLinkChannel ?? defaultChannel,
    id,
    eventId,
    isPass: isPassCard,
    checkIsChannelActive: isPassCard,
  });
  const { data: eventChannels } = useGetChannels({
    eventId,
    enabled: isSharedSubMenuClicked && eventId !== undefined,
  });
  const { data: offersChannels } = useGetActiveChannels({
    offerId: id,
    enabled: isSharedSubMenuClicked,
  });

  const { mutate: createGroup, isSuccess: isSuccessCreateGroup, data: dataCreateGroup } = useCreateGroup({ eventId });
  const { mutate: addOfferToGroup } = useAddOfferToGroup({ eventId, offerName: name });
  const { mutate: removeOfferFromGroup } = useRemoveOfferFromGroup({ eventId });
  const handleEdit = useCallback(() => {
    if (isPassCard && !isToggleActive && !isPreset) return;
    if (onEdit) onEdit(id);
    if (onClick) onClick();
  }, [onEdit, onClick, id, isPassCard, isToggleActive, isPreset]);

  const handleDuplicate = useCallback(() => {
    if (onDuplicate) onDuplicate();
  }, [onDuplicate]);

  const handleDelete = useCallback(() => {
    if (onDelete) onDelete(id);
  }, [onDelete, id]);

  const handleDeletePass = useCallback(() => {
    if (onDeletePass) onDeletePass(id);
  }, [onDeletePass, id]);

  const handleUnassign = useCallback(() => {
    if (onUnassign) onUnassign(id);
  }, [onUnassign, id]);

  // PassCard handlers
  const handleViewHolders = useCallback(() => {
    if (onClickViewHolders) onClickViewHolders();
  }, [onClickViewHolders]);

  const handleAddHolder = useCallback(() => {
    if (onClickAddHolder) onClickAddHolder();
  }, [onClickAddHolder]);

  const handleViewEvents = useCallback(() => {
    if (onClickViewEvents) onClickViewEvents();
  }, [onClickViewEvents]);

  const toggleClickHandler = (value: boolean, e: React.KeyboardEvent | React.MouseEvent) => {
    e.stopPropagation();
    setIsConfirmToggleModalOpen(true);
  };

  const handleClickConfirmToggle = () => {
    onToggleClick?.({ isToggleActive, passId: id });
    setIsToggleActive((prev) => !prev);
    setIsConfirmToggleModalOpen(false);
  };

  const handlePassShareLink = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.getOfferChannelsById, id],
    });
    setIsSharedSubMenuClicked(true);
  }, [queryClient, id]);

  const resetSharePaymentLinkStates = () => {
    setSearchPaymentLinkChannelsValue('');
    setSelectedPaymentLinkChannel(undefined);
    setIsSharedSubMenuClicked(false);
    setPaymentLinkChannels(undefined);
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.getOfferChannelsById, id],
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.getOfferChannelsById, eventId],
    });
  };

  const handleCloseSelectPaymentLinkChannelModal = () => {
    setIsOpenSelectPaymentLinkChannelModal(false);
    resetSharePaymentLinkStates();
  };

  const handleClickSharedLink = useCallback(() => {
    setIsSharedSubMenuClicked(true);
  }, []);

  useEffect(() => {
    if (!isShareLinkclick) {
      if (isOpenSelectPaymentLinkChannelModal) handleCloseSelectPaymentLinkChannelModal();
      else resetSharePaymentLinkStates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShareLinkclick]);

  useEffect(() => {
    if (isSharedSubMenuClicked) {
      const enabledEventChannels = eventChannels?.filter((eventChannel) => eventChannel.isEnabled);
      if (offersChannels?.channels.length === 0) setPaymentLinkChannels(enabledEventChannels);
      else {
        const channels = enabledEventChannels?.filter((eventChannel) => {
          return offersChannels?.channels.some((offerChannel) => offerChannel.id === eventChannel.channel.id);
        });
        setPaymentLinkChannels(channels);
      }
      setIsShareLinkclick(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSharedSubMenuClicked, eventChannels, offersChannels]);

  const handleShareLink = useCallback(() => {
    setIsShareLinkclick(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setIsShareLinkclick]);

  const setDefaultPaymentLinkChannel = (channels: EventChannel[]) => {
    const defaultChannelIndex = channels.findIndex((paymentLinkChannel) => {
      const hasDefaultChannel = paymentLinkChannel.channel.id === defaultChannel?.id;
      return hasDefaultChannel;
    });
    if (defaultChannelIndex >= 0) setSelectedPaymentLinkChannel(channels[defaultChannelIndex].channel);
    else setSelectedPaymentLinkChannel(undefined);
  };

  useEffect(() => {
    if (paymentLinkChannels) {
      if (paymentLinkChannels.length === 1) {
        const channel = paymentLinkChannels[0].channel;
        setSelectedPaymentLinkChannel(channel);
      } else if (!selectedPaymentLinkChannel) {
        setDefaultPaymentLinkChannel(paymentLinkChannels);
      }

      setIsOpenSelectPaymentLinkChannelModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentLinkChannels]);

  const handleSearchPaymentLinkChange = (value: string) => {
    setSearchPaymentLinkChannelsValue(value);
  };

  const handleAddGroup = useCallback(() => {
    setIsOpenAddGroupModal(true);
  }, []);

  const handleRemoveFromGroup = useCallback(() => {
    if (!groupId) return;
    removeOfferFromGroup({ groupId, offerId: id });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, id]);

  const admissionType = isPassCard ? ADMISSION_CARD_TYPE.pass : ADMISSION_CARD_TYPE.offer;

  const ACTIONS = useAdmissionCardActions({
    type: admissionType,
    isPreset: isPreset ?? false,
    isPastEvent: isPastEvent ?? false,
    isSharePaymentLinkActive,
    isOpenForAll: isOpenForAll ?? false,
    deleteIsUnassign: deleteIsUnassign ?? false,
    isToggleActive: isToggleActive ?? false,
    isGroupCard,
    isEnabledSection: sectionId !== undefined,
    onEdit,
    onDelete,
    onDeletePass,
    onDuplicate,
    onUnassign,
    onClickViewHolders,
    onClickAddHolder,
    onClickViewEvents,
    handleEdit,
    handleDelete,
    handleDeletePass,
    handleDuplicate,
    handleUnassign,
    handleViewHolders,
    handleViewEvents,
    handleAddHolder,
    handleShareLink: isPassCard ? handlePassShareLink : handleClickSharedLink,
    handleAddGroup,
    handleRemoveFromGroup,
    t,
  });

  const cardOpacity = status === ADMISSION_STATUS_HIDDEN || status === ADMISSION_STATUS_IN_REVIEW ? 'opacity-50' : '';

  const handleCloseAddGroupModal = useCallback(() => {
    setIsOpenAddGroupModal(false);
    setSelectedGroup(undefined);
    setErrorAddGroup('');
  }, []);

  const handleGroupSelectionChange = (selection: { id?: string; name: string }) => {
    if (!sectionGroups) {
      setSelectedGroup(selection);
      setErrorAddGroup('');
      return;
    }

    const error = getGroupNameErrorForSelection({
      name: selection.name ?? '',
      language: i18n.language,
      sectionGroups,
      t,
      excludeGroupId: selection.id,
    });

    setErrorAddGroup(error);
    setSelectedGroup(selection);
  };

  const handleAddGroupSubmit = (group: { id?: string; name: string }) => {
    if (!group) return;
    if (group.id) {
      addOfferToGroup({ groupId: group.id, offerId: id });
      handleCloseAddGroupModal();
    } else if (sectionId) {
      createGroup({
        sectionId,
        group: {
          name: {
            ca: null,
            pt: null,
            en: null,
            de: null,
            fr: null,
            it: null,
            es: null,
            [i18n.language]: group.name,
          },
        },
      });
    }
  };

  useEffect(() => {
    if (isSuccessCreateGroup && dataCreateGroup) {
      addOfferToGroup({ groupId: dataCreateGroup.id, offerId: id });
      handleCloseAddGroupModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessCreateGroup, dataCreateGroup]);

  const groups = sectionGroups?.map((group) => ({ id: group.id, label: group.displayName }));

  const currentLanguageName = t(getLanguageTranslationKey(i18n.language)).toLowerCase();
  const newOptionHelperText = t('admissionGroups:addAdmissionToGroupHelper', { language: currentLanguageName });

  if (isDragging && !dragHandle) {
    return <AdmissionPlaceholderDragStart height={getInitialHeight()} />;
  }

  return (
    <>
      {insertPosition === 'before' && <AdmissionPlaceholderDragEnd height={destinationHeight} />}
      <div
        ref={containerRef}
        className={classNames({
          'animate-pulse-delete pointer-events-none': isDeleting,
          'animate-pulse-create pointer-events-none': isCreating || isEditing,
        })}>
        <div
          id={id}
          className={classNames(
            'flex flex-col gap-4 justify-between cursor-pointer',
            'py-5 px-4 sm:p-6 @sm:py-5 @sm:px-4',
            'border border-extra-soft hover:border-medium rounded-md bg-surface-primary ',
            {
              'shadow-xxl opacity-90': isInDragOverlay,
              'shadow-xl': !isInDragOverlay,
            }
          )}
          ref={setNodeRef}
          role='menuitem'
          tabIndex={0}
          onClick={handleEdit}
          onKeyDown={undefined}>
          <div className='flex flex-row w-full gap-2 sm:gap-0 @sm:gap-2 min-w-0'>
            <AdmissionCardInfo
              {...{
                id,
                quantity,
                soldItems,
                tags,
                status,
                accessValidity,
                isPreset,
                cardOpacity,
                color,
                name,
                description,
                isDateVisible,
                formattedSalesEndsDate,
                guestCapacity,
                checked,
                listeners,
                attributes,
                isSelectActive,
                isMultiSelectActive,
                onChange,
                onlinePrice,
                ddpPrice,
                formattedOnlinePrice,
                formatedDdpPrice,
                isDragActive: isDragActive ?? isDragging,
                hideStockInfo,
              }}
            />
            <div className='flex flex-col-reverse justify-between sm:flex-row @sm:flex-col-reverse items-end sm:items-center sm:gap-8 shrink-0'>
              <div className={`text-right text-subtitle-m text-primary sm:ml-8 ${cardOpacity}`}>{formattedPrice}</div>
              {showSubmenuIcon && <SubmenuIcon options={ACTIONS} zIndexClassName={submenuZIndexClass} />}
            </div>
          </div>
          {isPassCard && passHolders && (
            <>
              <div className='border-t border-extra-soft' />
              <div className='flex flex-row items-center justify-between'>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewHolders();
                  }}
                  className='flex items-center gap-1'>
                  <span className='text-secondary text-body-xs'>{t('passes:activeLabel')}</span>
                  <span className='text-primary text-subtitle-s'>
                    {passHolders.active || 0}/{(passHolders.active || 0) + (passHolders.inactive || 0)}
                  </span>
                </button>
                {!isPreset && (
                  <SwitchToggle
                    active={isToggleActive}
                    loading={false}
                    onToggleSwitch={(value: boolean, e: React.KeyboardEvent | React.MouseEvent) =>
                      toggleClickHandler(value, e)
                    }
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {isPassCard && (
        <ConfirmModal
          title={t('passes:confirmSaveEventPassActivateTitle')}
          isOpen={isConfirmToggleModalOpen}
          handleClickConfirm={handleClickConfirmToggle}
          handleClose={() => setIsConfirmToggleModalOpen(false)}
        />
      )}
      <SearchItemModal
        handleClose={handleCloseSelectPaymentLinkChannelModal}
        isOpen={isOpenSelectPaymentLinkChannelModal}
        placeholder={t('offers:admissionTicketSalesChannelsSearchLabel')}
        title={t('offers:selectChannelModalTitle')}
        buttonPrimaryText={t('common:doneButton')}
        buttonSecondaryText={t('common:discard')}
        isButtonPrimaryDisabled={!selectedPaymentLinkChannel}
        handlePrimaryButton={handleShareLink}
        handleSecondaryButton={handleCloseSelectPaymentLinkChannelModal}
        handleSearch={handleSearchPaymentLinkChange}
        showCloseIconButton={true}
        debounceInputVariant={DEBOUNCE_VARIANT_DARK}>
        <ChannelList
          handleChange={setSelectedPaymentLinkChannel}
          selectedChannel={selectedPaymentLinkChannel}
          channels={paymentLinkChannels}
          channelSearchValue={searchPaymentLinkChannelsValue}
        />
      </SearchItemModal>
      <RadioListModal
        isOpen={isOpenAddGroupModal}
        onClose={handleCloseAddGroupModal}
        options={groups}
        value={selectedGroup ?? undefined}
        onChange={handleGroupSelectionChange}
        primaryButtonText={t('admissionGroups:addAdmissionToGroupModalButton')}
        onPrimaryClick={handleAddGroupSubmit}
        title={t('admissionGroups:addAdmissionToGroupModalTitle', {
          AdmissionType: admissionTypeLabel ?? admissionType,
        })}
        subtitle={subtitleModalAddToGroup}
        enableNewOption={true}
        newOptionPlaceholder={t('admissionGroups:createGroupModalInputLabel')}
        newMaxLength={MAX_LENGTH_GROUP_NAME}
        newError={errorAddGroup}
        newOptionHelper={newOptionHelperText}
        isPrimaryDisabled={!!errorAddGroup || !selectedGroup?.name}
        newOptionLabel={t('admissionGroups:addAdmissionToGroupNewGroupLabel')}
      />
      {insertPosition === 'after' && <AdmissionPlaceholderDragEnd height={destinationHeight} />}
    </>
  );
};
